import { type ReactNode, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SettingsSection } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { CalendarLinkErrorDialog } from '@/components/CalendarLinkErrorDialog.tsx';
import { DisconnectCalendarDialog } from '@/components/DisconnectCalendarDialog.tsx';
import { SlackConnectionRow } from '@/components/SlackConnectionRow.tsx';
import { StatusLine } from '@/components/StatusLine.tsx';
import { CalendarConnectionRow } from '@/components/settings/CalendarConnectionRow.tsx';
import {
  useCalendarConnections,
  type CalendarAccount,
} from '@/components/settings/useCalendarConnections.ts';
import { INTEGRATION_PROVIDERS, type IntegrationProvider } from '@/lib/integrations.ts';
import { canLinkSlack } from '@/lib/slackInstallation.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/connections')({
  /*
   * With nothing this person may connect, there is no tab and nothing on the
   * page: a link or a bookmark to it lands on the first tab instead.
   */
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData({
      queryKey: ['currentUser'],
      queryFn: fetchCurrentUser,
    });

    if (!user.calendar_enabled && !canLinkSlack(user)) {
      throw redirect({ to: '/settings/profile', replace: true });
    }
  },
  component: ConnectionsRoute,
  /*
   * A provider's refusal comes back to this page as `?error=` — the flow set
   * itself up that way (`errorCallbackURL` in `startCalendarLink`), and until
   * this param was read, the refusal arrived and vanished without a word.
   */
  validateSearch: (search: Record<string, unknown>): ConnectionsSearch => ({
    error: typeof search.error === 'string' ? search.error : undefined,
    slack: typeof search.slack === 'string' ? search.slack : undefined,
  }),
});

interface ConnectionsSearch {
  /** The code a provider's refusal bounced back with. */
  error?: string;
  /** How an "Add to Slack" ended, as the API sent the browser back. */
  slack?: string;
}

/*
 * Reading the search is the route's job, so the page itself stays renderable.
 * Dismissing clears `?error=` from the address — the dialog closes because the
 * code is gone, and a reload or a shared link does not resurrect a refusal
 * that was already read.
 */
function ConnectionsRoute() {
  const { error, slack } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  return (
    <ConnectionSettingsPage
      showCalendar={user?.calendar_enabled !== false}
      showSlack={canLinkSlack(user)}
      slackResult={slack ?? null}
      linkError={error ?? null}
      onDismissLinkError={() => void navigate({ search: {}, replace: true })}
    />
  );
}

const PROVIDER_NAMES: Record<IntegrationProvider, () => string> = {
  microsoft: m.connections_microsoft,
  google: m.connections_google,
};

export interface ConnectionSettingsPageProps {
  /** The calendar rows follow the organisation's calendar switch. */
  showCalendar?: boolean;
  /** The organisation's Slack row, for an admin. */
  showSlack?: boolean;
  /** The `?slack=` result an "Add to Slack" came back with, or null. */
  slackResult?: string | null;
  /** The `?error=` code a provider bounced back with, or null when there is none. */
  linkError?: string | null;
  /** Called when the refusal has been read; the owner clears it from the address. */
  onDismissLinkError?: () => void;
}

export function ConnectionSettingsPage({
  showCalendar = true,
  showSlack = false,
  slackResult = null,
  linkError = null,
  onDismissLinkError = () => {},
}: ConnectionSettingsPageProps) {
  const connections = useCalendarConnections(showCalendar);
  /* Which calendar the confirm is about. Null means the dialog is closed. */
  const [confirming, setConfirming] = useState<CalendarAccount | null>(null);

  if (showCalendar && connections.isPending) return null;
  if (showCalendar && connections.isError) {
    return <p className='text-base text-ink-muted'>{m.connections_load_failed()}</p>;
  }

  const slackStatus = slackResultMessage(slackResult);

  const calendarRows = connections.loaded
    ? INTEGRATION_PROVIDERS.map((provider) => (
        <CalendarConnectionRow
          key={provider}
          provider={provider}
          name={PROVIDER_NAMES[provider]()}
          connections={connections}
          onDisconnect={setConfirming}
        />
      ))
    : undefined;

  return (
    <>
      <StatusLine
        message={
          connections.failed ? m.connections_action_failed() : slackStatus?.message
        }
        tone={connections.failed || slackStatus?.tone === 'danger' ? 'danger' : 'muted'}
      />

      <ConnectionSections
        showCalendar={showCalendar}
        showSlack={showSlack}
        calendarRows={calendarRows}
      />

      {/*
       * One dialog for the page rather than one per row: only one calendar can
       * be being disconnected at a time, and which one is the state above.
       * Failure keeps its existing home in the status line at the top — the
       * dialog is closed by then, and two places saying it is one too many.
       */}
      <DisconnectCalendarDialog
        providerName={confirming === null ? null : PROVIDER_NAMES[confirming.provider]()}
        onConfirm={() => {
          if (confirming !== null) connections.disconnect(confirming);
          setConfirming(null);
        }}
        onCancel={() => setConfirming(null)}
      />

      <CalendarLinkErrorDialog code={linkError} onClose={onDismissLinkError} />
    </>
  );
}

interface ConnectionSectionsProps {
  showCalendar: boolean;
  showSlack: boolean;
  calendarRows: ReactNode;
}

/*
 * Slack is set for the whole organisation, a calendar per person, so once
 * both can show they get a section each. Without Slack the page keeps its
 * single card.
 */
function ConnectionSections({
  showCalendar,
  showSlack,
  calendarRows,
}: ConnectionSectionsProps) {
  if (!showSlack) {
    return (
      <SettingsSection title={m.settings_tab_connections()}>
        {calendarRows}
      </SettingsSection>
    );
  }

  return (
    <>
      {showCalendar && (
        <SettingsSection title={m.connections_section_calendar()}>
          {calendarRows}
        </SettingsSection>
      )}
      <SettingsSection
        title={m.connections_section_chat()}
        description={m.connections_section_chat_desc()}
      >
        <SlackConnectionRow />
      </SettingsSection>
    </>
  );
}

/*
 * A cancelled consent screen was the admin's own choice, so it says nothing.
 * Every other failure has one remedy, trying again, except a workspace that
 * belongs to another organisation: only Wundo can sort that out.
 */
function slackResultMessage(
  result: string | null,
): { message: string; tone: 'muted' | 'danger' } | null {
  switch (result) {
    case null:
    case 'denied':
      return null;
    case 'connected':
      return { message: m.connections_slack_result_connected(), tone: 'muted' };
    case 'team_taken':
      return { message: m.connections_slack_result_team_taken(), tone: 'danger' };
    default:
      return { message: m.connections_slack_result_failed(), tone: 'danger' };
  }
}
